/**
 * HireInspectionTemplatesTab — admin-managed pre-hire / post-hire inspection
 * checklists, slotted into the Forms page as a third tab.
 *
 * One template per (inspection_type, equipment_type). Editable defaults,
 * soft-delete (archive) with a Restore action.
 *
 * Data lives in MongoDB collection `hire_inspection_templates` and is
 * separate from the generic Forms data — different domain, different lifecycle.
 */
import { useState, useEffect, useCallback, useMemo } from 'react';
import { useAuth } from '../context/AuthContext';
import { toast } from 'sonner';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from './ui/card';
import { Badge } from './ui/badge';
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetFooter } from './ui/sheet';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { ConfirmationDialog } from './ui/confirmation-dialog';
import { ClipboardList, Plus, Pencil, Archive, ArchiveRestore, GripVertical, Trash2 } from 'lucide-react';

const EMPTY_FORM = {
  name: '',
  inspection_type: 'pre_hire',
  equipment_type: '__universal__', // sentinel; mapped to null on save
  checklist_items: [],
};

const HireInspectionTemplatesTab = () => {
  const { api } = useAuth();
  const [templates, setTemplates] = useState([]);
  const [equipmentTypes, setEquipmentTypes] = useState([]);
  const [statusFilter, setStatusFilter] = useState('active');
  const [loading, setLoading] = useState(true);

  // Drawer / form state
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [editingTemplate, setEditingTemplate] = useState(null);
  const [form, setForm] = useState(EMPTY_FORM);
  const [saving, setSaving] = useState(false);

  // Archive confirmation
  const [archiveDialogOpen, setArchiveDialogOpen] = useState(false);
  const [templateToArchive, setTemplateToArchive] = useState(null);

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      const [tplRes, eqRes] = await Promise.all([
        api.get(`/hire-inspection-templates?status=${statusFilter}`),
        api.get('/equipment-types'),
      ]);
      setTemplates(tplRes.data || []);
      setEquipmentTypes(eqRes.data || []);
    } catch {
      toast.error('Failed to load inspection templates');
    } finally {
      setLoading(false);
    }
  }, [api, statusFilter]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const handleOpenDrawer = (template = null) => {
    if (template) {
      setEditingTemplate(template);
      setForm({
        name: template.name || '',
        inspection_type: template.inspection_type || 'pre_hire',
        equipment_type: template.equipment_type || '__universal__',
        checklist_items: (template.checklist_items || []).map((it) => ({
          id: it.id || crypto.randomUUID(),
          category: it.category || '',
          label: it.label || '',
        })),
      });
    } else {
      setEditingTemplate(null);
      setForm({ ...EMPTY_FORM, checklist_items: [] });
    }
    setDrawerOpen(true);
  };

  const updateItem = (idx, patch) => {
    setForm((f) => ({
      ...f,
      checklist_items: f.checklist_items.map((it, i) => (i === idx ? { ...it, ...patch } : it)),
    }));
  };

  const addItem = () => {
    setForm((f) => ({
      ...f,
      checklist_items: [
        ...f.checklist_items,
        { id: crypto.randomUUID(), category: '', label: '' },
      ],
    }));
  };

  const removeItem = (idx) => {
    setForm((f) => ({
      ...f,
      checklist_items: f.checklist_items.filter((_, i) => i !== idx),
    }));
  };

  const handleSave = async () => {
    if (!form.name.trim()) {
      toast.error('Template name is required');
      return;
    }
    const cleanItems = form.checklist_items
      .map((it) => ({ id: it.id, category: it.category?.trim() || null, label: it.label?.trim() || '' }))
      .filter((it) => it.label);
    if (cleanItems.length === 0) {
      toast.error('Add at least one checklist item');
      return;
    }
    const payload = {
      name: form.name.trim(),
      inspection_type: form.inspection_type,
      equipment_type: form.equipment_type === '__universal__' ? null : form.equipment_type,
      checklist_items: cleanItems,
    };

    setSaving(true);
    try {
      if (editingTemplate) {
        await api.put(`/hire-inspection-templates/${editingTemplate.id}`, payload);
        toast.success('Template updated');
      } else {
        await api.post('/hire-inspection-templates', payload);
        toast.success('Template created');
      }
      setDrawerOpen(false);
      fetchData();
    } catch (error) {
      const detail = error.response?.data?.detail;
      toast.error((typeof detail === 'string' && detail) || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  const handleArchive = (template) => {
    setTemplateToArchive(template);
    setArchiveDialogOpen(true);
  };

  const handleConfirmArchive = async () => {
    if (!templateToArchive) return;
    try {
      await api.delete(`/hire-inspection-templates/${templateToArchive.id}`);
      toast.success('Template archived');
      setArchiveDialogOpen(false);
      setTemplateToArchive(null);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Archive failed');
    }
  };

  const handleRestore = async (template) => {
    try {
      await api.post(`/hire-inspection-templates/${template.id}/restore`);
      toast.success('Template restored');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Restore failed');
    }
  };

  const grouped = useMemo(() => {
    const pre = templates.filter((t) => t.inspection_type === 'pre_hire');
    const post = templates.filter((t) => t.inspection_type === 'post_hire');
    return { pre, post };
  }, [templates]);

  if (loading) {
    return <div className="animate-pulse h-64 bg-muted rounded" />;
  }

  return (
    <div className="space-y-4" data-testid="hire-inspection-templates-tab">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
        <div>
          <h3 className="text-lg font-semibold flex items-center gap-2">
            <ClipboardList className="h-5 w-5 text-[#ffd108]" />
            Hire Inspection Templates
          </h3>
          <p className="text-sm text-muted-foreground">
            Define pre-hire and post-hire checklists per equipment type. Auto-applied when dry-hire jobs are created or off-hired.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Select value={statusFilter} onValueChange={setStatusFilter}>
            <SelectTrigger className="w-[140px]" data-testid="template-status-filter">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="active">Active</SelectItem>
              <SelectItem value="archived">Archived</SelectItem>
              <SelectItem value="all">All</SelectItem>
            </SelectContent>
          </Select>
          <Button onClick={() => handleOpenDrawer()} className="btn-volt" data-testid="new-template-btn">
            <Plus className="h-4 w-4 mr-2" />
            New Template
          </Button>
        </div>
      </div>

      {[
        { key: 'pre', label: 'Pre-hire Templates', list: grouped.pre },
        { key: 'post', label: 'Post-hire Templates', list: grouped.post },
      ].map((section) => (
        <div key={section.key} className="space-y-2">
          <div className="text-sm font-medium text-muted-foreground uppercase tracking-wide">
            {section.label} ({section.list.length})
          </div>
          {section.list.length === 0 ? (
            <Card>
              <CardContent className="py-6 text-center text-sm text-muted-foreground">
                No {section.label.toLowerCase()} for this filter.
              </CardContent>
            </Card>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
              {section.list.map((template) => (
                <Card
                  key={template.id}
                  className="relative"
                  data-testid={`template-card-${template.id}`}
                >
                  {template.is_default && (
                    <Badge className="absolute top-2 right-2 bg-[#ffd108]/20 text-[#ffd108] border border-[#ffd108]/40 hover:bg-[#ffd108]/35">
                      Default
                    </Badge>
                  )}
                  <CardHeader className="pb-3">
                    <CardTitle className="text-base pr-16">{template.name}</CardTitle>
                    <CardDescription className="flex items-center gap-2 flex-wrap">
                      <Badge variant="outline" className="text-xs">
                        {template.inspection_type === 'pre_hire' ? 'Pre-hire' : 'Post-hire'}
                      </Badge>
                      <Badge variant="outline" className="text-xs">
                        {template.equipment_type || 'Universal (fallback)'}
                      </Badge>
                      {template.archived && (
                        <Badge
                          variant="outline"
                          className="text-xs bg-muted/30 text-muted-foreground border-muted-foreground/40"
                          data-testid={`archived-badge-${template.id}`}
                        >
                          Archived
                        </Badge>
                      )}
                    </CardDescription>
                  </CardHeader>
                  <CardContent>
                    <div className="text-sm text-muted-foreground mb-3">
                      {template.checklist_items?.length || 0} checklist items
                    </div>
                    <div className="flex justify-end gap-2">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => handleOpenDrawer(template)}
                        data-testid={`edit-template-${template.id}`}
                      >
                        <Pencil className="h-3.5 w-3.5 mr-1" />
                        Edit
                      </Button>
                      {template.archived ? (
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => handleRestore(template)}
                          data-testid={`restore-template-${template.id}`}
                        >
                          <ArchiveRestore className="h-3.5 w-3.5 mr-1" />
                          Restore
                        </Button>
                      ) : (
                        <Button
                          variant="outline"
                          size="sm"
                          className="text-destructive hover:text-destructive"
                          onClick={() => handleArchive(template)}
                          data-testid={`archive-template-${template.id}`}
                        >
                          <Archive className="h-3.5 w-3.5 mr-1" />
                          Archive
                        </Button>
                      )}
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </div>
      ))}

      {/* Editor Drawer */}
      <Sheet open={drawerOpen} onOpenChange={setDrawerOpen}>
        <SheetContent side="right" className="w-[640px] sm:max-w-[640px] overflow-y-auto">
          <SheetHeader className="pb-4 border-b">
            <SheetTitle className="font-heading text-xl">
              {editingTemplate ? 'Edit Template' : 'New Inspection Template'}
            </SheetTitle>
          </SheetHeader>
          <div className="space-y-5 py-4">
            <div className="space-y-2">
              <Label htmlFor="tpl-name">Template Name</Label>
              <Input
                id="tpl-name"
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                placeholder="e.g. Excavator 20t — Pre-hire"
                data-testid="template-name-input"
              />
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-2">
                <Label>Form Type</Label>
                <Select
                  value={form.inspection_type}
                  onValueChange={(v) => setForm({ ...form, inspection_type: v })}
                >
                  <SelectTrigger data-testid="template-inspection-type">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="pre_hire">Pre-hire</SelectItem>
                    <SelectItem value="post_hire">Post-hire</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Equipment Type</Label>
                <Select
                  value={form.equipment_type}
                  onValueChange={(v) => setForm({ ...form, equipment_type: v })}
                >
                  <SelectTrigger data-testid="template-equipment-type">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="__universal__">Universal (fallback)</SelectItem>
                    {equipmentTypes.map((et) => (
                      <SelectItem key={et} value={et}>
                        {et}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <Label>Checklist Items</Label>
                <Button variant="outline" size="sm" onClick={addItem} data-testid="add-item-btn">
                  <Plus className="h-3.5 w-3.5 mr-1" />
                  Add Item
                </Button>
              </div>
              {form.checklist_items.length === 0 ? (
                <Card className="border-dashed">
                  <CardContent className="py-6 text-center text-sm text-muted-foreground">
                    No items yet — click "Add Item" to start building the checklist.
                  </CardContent>
                </Card>
              ) : (
                <div className="space-y-2">
                  {form.checklist_items.map((it, idx) => (
                    <div
                      key={it.id}
                      className="flex items-start gap-2 p-2 border rounded-md bg-muted/20"
                      data-testid={`checklist-item-row-${idx}`}
                    >
                      <GripVertical className="h-4 w-4 text-muted-foreground mt-3 cursor-not-allowed" />
                      <div className="flex-1 grid grid-cols-3 gap-2">
                        <Input
                          placeholder="Category (optional)"
                          value={it.category || ''}
                          onChange={(e) => updateItem(idx, { category: e.target.value })}
                          className="text-sm"
                        />
                        <Input
                          placeholder="Item label"
                          value={it.label || ''}
                          onChange={(e) => updateItem(idx, { label: e.target.value })}
                          className="text-sm col-span-2"
                          data-testid={`checklist-item-label-${idx}`}
                        />
                      </div>
                      <Button
                        variant="ghost"
                        size="sm"
                        className="text-destructive"
                        onClick={() => removeItem(idx)}
                        data-testid={`remove-item-${idx}`}
                      >
                        <Trash2 className="h-3.5 w-3.5" />
                      </Button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
          <SheetFooter className="border-t pt-4">
            <Button variant="outline" onClick={() => setDrawerOpen(false)}>Cancel</Button>
            <Button className="btn-volt" onClick={handleSave} disabled={saving} data-testid="save-template-btn">
              {saving ? 'Saving…' : editingTemplate ? 'Save Changes' : 'Create Template'}
            </Button>
          </SheetFooter>
        </SheetContent>
      </Sheet>

      <ConfirmationDialog
        open={archiveDialogOpen}
        onOpenChange={setArchiveDialogOpen}
        title="Archive Template"
        description={
          templateToArchive
            ? `Archive "${templateToArchive.name}"? Existing inspections already created from this template are unaffected. Archive can be reversed from the Archived filter.`
            : ''
        }
        confirmText="Archive"
        variant="archive"
        onConfirm={handleConfirmArchive}
        testId="archive-template-dialog"
      />
    </div>
  );
};

export default HireInspectionTemplatesTab;
